import {DataTableActionHeader, DataTableCard, TablePending} from "../../components/DataTable/DataTable"
import {startCase} from "lodash"
import React, {useEffect, useMemo} from "react"
import {IncomingAchStatus, findIncomingAch, IncomingAchManualReviewInfo} from "../../resources/incomingAch"
import {ErrorDocument} from "../../resources/common"
import {useAccessToken, useUserType} from "../../services/auth"
import {useQueryState} from "use-location-state"
import {Cents} from "../../resources/transaction"
import {usePaging} from "../../hooks/usePaging"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {Org, findOrgs} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {Filter} from "../../components/Filter/Filter"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../../components/DatePicker/DatePicker"
import {CurrencyRangeFilter} from "../../components/Filter/CurrencyRangeFilter"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import SearchBox from "../../components/SearchBox/SearchBox"
import {OrgFilter} from "../../components/OrgFilter/OrgFilter"
import {AllowedIncomingAchColumns} from "./IncomingAch.types"
import IncomingAchsTable from "./IncomingAchsTable"

interface IncomingAchsProps {
	accountId?: string
	token?: string
	limit?: number
	includedColumns: AllowedIncomingAchColumns[]
	enableTitle?: boolean
	fullHeight?: boolean
	enableOrgFilter?: boolean
	enableStatusFilter?: boolean
	enableReviewReasonFilter?: boolean
	enableAmountFilter?: boolean
	enableDateFilter?: boolean
	enablePaging?: boolean
	enableExport?: boolean
	title?: string
}

export default function IncomingAchs({
	limit = 25,
	token,
	includedColumns,
	fullHeight,
	enableTitle,
	enableOrgFilter = false,
	enableStatusFilter = false,
	enableReviewReasonFilter = false,
	enableAmountFilter = false,
	enableDateFilter = false,
	enablePaging = true,
	title = "Incoming Achs",
}: IncomingAchsProps) {
	const prefix = "incachs"
	const accessToken = token ?? useAccessToken()
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [filteredReviewReasons, setFilteredReviewReasons] = useQueryState<IncomingAchManualReviewInfo[]>(
		`${prefix}-filter[reviewReasons]`,
		[IncomingAchManualReviewInfo.NameMismatch, IncomingAchManualReviewInfo.IAT]
	)
	const [statuses, setStatuses] = useQueryState("filter[status]", [IncomingAchStatus.PendingReview])
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")

	const onSearch = (searchTerm: string) => {
		setQuery(searchTerm)
		reset(limit)
	}
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search by Account/Customer/Amount", 500, onSearch)
	const statusesOptions = new Map(
		[
			IncomingAchStatus.PendingReview,
			IncomingAchStatus.Processed,
			IncomingAchStatus.MarkedForReturn,
			IncomingAchStatus.Warehoused,
			IncomingAchStatus.Returned,
			IncomingAchStatus.Errored,
		].map((v) => [v, startCase(v)])
	)

	const reviewReasonsOptions = new Map(
		[IncomingAchManualReviewInfo.IAT, IncomingAchManualReviewInfo.NameMismatch, IncomingAchManualReviewInfo.OFAC].map(
			(v) => [v, startCase(v)]
		)
	)
	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findIncomingAch(
				query,
				accessToken,
				offset,
				limit,
				sortBy,
				statuses,
				filteredOrgs,
				filteredReviewReasons,
				since,
				until,
				fromAmount,
				toAmount
			),
		(x) => x.data.length,
		[
			since,
			until,
			[...statuses, ...filteredOrgs, ...filteredReviewReasons].join(","),
			fromAmount,
			toAmount,
			query,
			sortBy,
		],
		`${prefix}-`
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])
	const userType = useUserType()

	const orgs =
		userType === "unit" || userType === "bank"
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const reviewReasonFilter = (
		<Filter
			statuses={filteredReviewReasons}
			setStatuses={setFilteredReviewReasons}
			onFilterFunc={() => reset(limit)}
			options={reviewReasonsOptions}
			title="Review Reason"
		/>
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={setFromAmount}
			to={toAmount}
			setTo={setToAmount}
		/>
	)

	function toggleSort(columnName: string) {
		const sortParam = sortBy == `-${columnName}` ? columnName : `-${columnName}`
		setSortBy(sortParam)
		reset(limit)
	}

	return (
		<DataTableCard className={"payments-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				dateTimePicker={enableDateFilter ? dateTime : null}
				filters={[
					...(enableReviewReasonFilter ? [reviewReasonFilter] : []),
					...(enableOrgFilter ? [orgFilter] : []),
					...(enableStatusFilter ? [statusFilter] : []),
					...(enableAmountFilter ? [amountFilter] : []),
				]}
				searchBox={searchBox}
				title={enableTitle ? title : null}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: incomingAchs}) => {
					return (
						<IncomingAchsTable
							incomingAchs={incomingAchs.data}
							meta={incomingAchs.meta}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={enablePaging}
							include={incomingAchs.included}
							fullHeight={fullHeight}
							includedColumns={includedColumns}
							sortFunction={toggleSort}
							sortBy={sortBy}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
